/**
 * GroundMarks — shock rings and scorch decals, pooled and flat on the deck.
 *
 * A shock ring is the ground's answer to an impact: a bright band that punches
 * out from the point and brakes (ease-out cubic), dying as it expands so it is
 * never a hoop that sits there. Additive, HDR-tinted so it blooms with the
 * sparks above it, and drawn in the FLOOR-SAFE colour the caller gives it —
 * anything painted on the deck goes through `telegraphHex`, because the deck
 * already spends red on hazard and green on mend.
 *
 * A scorch is what stays behind: a dark, blotchy disc that lives ~14 s and
 * fades over its last quarter. It sits under the telegraph decals (render
 * order 3, just above the floor) so a live danger marker is never obscured by
 * the mark of an old one.
 *
 * Both are fixed pools with a cursor: when full, the oldest is reused. Nothing
 * allocates after construction, and no geometry is ever disposed by callers.
 */

import * as THREE from 'three/webgpu';
import { uv, float, vec2, vec3, smoothstep, uniform, fract, sin, dot, mix, floor as tslFloor } from 'three/tsl';
import { DECAL_LIFT, DECAL_STACK } from '../combat/decals.js';
import { easeOutCubic } from '../math.js';

const RING_SEGMENTS = 48;
/** Rings start here so they never pop in as a dot at the exact centre. */
const RING_START_FRAC = 0.22;

let _ringGeo = null;
let _decalGeo = null;

function ringGeometry() {
  if (!_ringGeo) _ringGeo = new THREE.RingGeometry(0.9, 1, RING_SEGMENTS).rotateX(-Math.PI / 2);
  return _ringGeo;
}
function decalGeometry() {
  if (!_decalGeo) _decalGeo = new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2);
  return _decalGeo;
}

function createRing() {
  const mat = new THREE.MeshBasicNodeMaterial();
  const tint = uniform(new THREE.Color(1, 1, 1));
  const fade = uniform(0);
  // A drawn line, not a glow: the tint fills the band, a white keyline runs
  // through its middle and both edges go to ink. Alpha-blended so it reads on
  // the bright deck (additive vanished there).
  // RingGeometry's uv is planar; the radial coordinate is recovered from it.
  // 0 at the band's centre line, 1 at either edge (the band spans r 0.9..1).
  const rr = uv().sub(0.5).mul(2).length();
  const across = rr.sub(0.9).div(0.1).sub(0.5).abs().mul(2).clamp(0, 1);
  const ink = mix(tint, vec3(0.02, 0.02, 0.05), 0.85);
  let col = mix(tint, mix(tint, vec3(1, 1, 1), 0.9), smoothstep(float(0.3), float(0.1), across));
  col = mix(col, ink, smoothstep(float(0.7), float(0.85), across));
  mat.colorNode = col;
  mat.opacityNode = smoothstep(float(1.0), float(0.92), across).mul(fade);
  mat.transparent = true;
  mat.depthWrite = false;
  mat.fog = false;
  const mesh = new THREE.Mesh(ringGeometry(), mat);
  mesh.visible = false;
  mesh.frustumCulled = false;
  mesh.renderOrder = 6;
  mesh.name = 'ground-marks:ring';
  return { mesh, tint, fade, t: 0, T: 0, radius: 1 };
}

function createDecal() {
  const mat = new THREE.MeshBasicNodeMaterial();
  const fade = uniform(0);
  const seed = uniform(0);
  // Near-black with a hint of warmth: pure black reads as a hole in the deck.
  mat.colorNode = vec3(0.03, 0.022, 0.018);
  const r = uv().sub(0.5).mul(2).length();
  const base = smoothstep(float(1.0), float(0.18), r);
  // Cheap cell noise so the edge is blotchy rather than a perfect circle.
  const cell = tslFloor(uv().mul(9.0)).add(seed);
  const n = fract(sin(dot(cell, vec2(127.1, 311.7))).mul(43758.5453));
  const blotch = float(0.5).add(smoothstep(float(0.25), float(0.75), n).mul(0.5));
  mat.opacityNode = base.mul(blotch).mul(0.82).mul(fade);
  mat.transparent = true;
  mat.depthWrite = false;
  const mesh = new THREE.Mesh(decalGeometry(), mat);
  mesh.visible = false;
  mesh.frustumCulled = false;
  mesh.renderOrder = 3;
  mesh.name = 'ground-marks:scorch';
  return { mesh, fade, seed, life: 0, T: 0 };
}

export class GroundMarks {
  constructor({ rings = 10, decals = 16 } = {}) {
    this.rings = [];
    this.decals = [];
    for (let i = 0; i < rings; i++) this.rings.push(createRing());
    for (let i = 0; i < decals; i++) this.decals.push(createDecal());
    this.ringCap = rings;
    this.decalCap = decals;
    this.ringCursor = 0;
    this.decalCursor = 0;
  }

  attach(scene) {
    for (const r of this.rings) scene.add(r.mesh);
    for (const d of this.decals) scene.add(d.mesh);
    return this;
  }

  /** Tier caps: how many of each pool may be in use. */
  setCaps({ rings, decals }) {
    this.ringCap = Math.max(1, Math.min(this.rings.length, rings ?? this.rings.length));
    this.decalCap = Math.max(1, Math.min(this.decals.length, decals ?? this.decals.length));
    if (this.ringCursor >= this.ringCap) this.ringCursor = 0;
    if (this.decalCursor >= this.decalCap) this.decalCursor = 0;
    for (let i = this.ringCap; i < this.rings.length; i++) this._killRing(this.rings[i]);
    for (let i = this.decalCap; i < this.decals.length; i++) this._killDecal(this.decals[i]);
  }

  /**
   * A ring expanding to `radius` over `duration` seconds. `r,g,b` are linear
   * and may exceed 1 — `gain` multiplies them so the band reaches the bloom.
   */
  ring(x, y, z, radius, r, g, b, duration = 0.4, gain = 1) {
    const ring = this.rings[this.ringCursor];
    this.ringCursor = (this.ringCursor + 1) % this.ringCap;
    ring.t = 0;
    ring.T = Math.max(0.05, duration);
    ring.radius = Math.max(0.2, radius);
    ring.tint.value.setRGB(r * gain, g * gain, b * gain);
    ring.fade.value = 1;
    ring.mesh.position.set(x, y + DECAL_LIFT + DECAL_STACK * 2, z);
    ring.mesh.scale.setScalar(ring.radius * RING_START_FRAC);
    ring.mesh.visible = true;
  }

  /** A scorch mark of `radius` living `life` seconds. */
  scorch(x, y, z, radius, life = 14) {
    const i = this.decalCursor;
    const decal = this.decals[i];
    this.decalCursor = (i + 1) % this.decalCap;
    decal.life = life;
    decal.T = life;
    decal.fade.value = 1;
    decal.seed.value = Math.random() * 100;
    // Stacked by slot so two overlapping scorches never z-fight.
    decal.mesh.position.set(x, y + DECAL_LIFT * 0.5 + i * 0.0008, z);
    decal.mesh.rotation.y = Math.random() * Math.PI * 2;
    decal.mesh.scale.setScalar(Math.max(0.3, radius));
    decal.mesh.visible = true;
  }

  update(dt) {
    for (const ring of this.rings) {
      if (!ring.mesh.visible) continue;
      ring.t += dt;
      const k = Math.min(1, ring.t / ring.T);
      if (k >= 1) {
        this._killRing(ring);
        continue;
      }
      const grow = RING_START_FRAC + (1 - RING_START_FRAC) * easeOutCubic(k);
      ring.mesh.scale.setScalar(ring.radius * grow);
      // Dies while expanding: (1 - k)^1.5 leaves quickly without a hard cut.
      ring.fade.value = Math.pow(1 - k, 1.5);
    }
    for (const decal of this.decals) {
      if (!decal.mesh.visible) continue;
      decal.life -= dt;
      if (decal.life <= 0) {
        this._killDecal(decal);
        continue;
      }
      const tail = decal.T * 0.25;
      decal.fade.value = decal.life < tail ? decal.life / tail : 1;
    }
  }

  _killRing(ring) {
    ring.mesh.visible = false;
    ring.fade.value = 0;
    ring.t = ring.T = 0;
  }

  _killDecal(decal) {
    decal.mesh.visible = false;
    decal.fade.value = 0;
    decal.life = 0;
  }

  clear() {
    for (const r of this.rings) this._killRing(r);
    for (const d of this.decals) this._killDecal(d);
    this.ringCursor = 0;
    this.decalCursor = 0;
  }

  get liveRings() {
    let n = 0;
    for (const r of this.rings) if (r.mesh.visible) n++;
    return n;
  }

  get liveDecals() {
    let n = 0;
    for (const d of this.decals) if (d.mesh.visible) n++;
    return n;
  }

  dispose() {
    for (const r of this.rings) {
      r.mesh.removeFromParent();
      r.mesh.material.dispose();
    }
    for (const d of this.decals) {
      d.mesh.removeFromParent();
      d.mesh.material.dispose();
    }
  }
}
